/**
 * Apply tenant policy to a Rainforest `allowed-methods` string (URL/embed config).
 * Strips method types disabled at the tenant level (Apple Pay, ACH). If the
 * result would be empty, falls back to CARD-only — card can't be disabled.
 */
export function resolvePatientFacingAllowedMethods(
  urlAllowedMethods: string | null | undefined,
  tenantAllowsApplePay: boolean,
  tenantAllowsAch: boolean = true,
): string | undefined {
  const trimmed = urlAllowedMethods?.trim();
  if (tenantAllowsApplePay !== false && tenantAllowsAch !== false) {
    return trimmed || undefined;
  }

  const defaultList = ["CARD", "ACH", "APPLE_PAY"];
  const parts = trimmed
    ? trimmed
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : defaultList;
  const filtered = parts.filter((p) => {
    const upper = p.toUpperCase();
    if (upper === "APPLE_PAY" && tenantAllowsApplePay === false) return false;
    if (upper === "ACH" && tenantAllowsAch === false) return false;
    return true;
  });
  if (filtered.length === 0) {
    return "CARD";
  }
  return filtered.join(",");
}

/**
 * Whether an `allowed-methods` string leaves nothing but a bank account (ACH).
 * Such a form never shows card, so it must start unsurcharged rather than wait
 * for a method switch that never comes.
 */
export function isBankOnly(allowedMethods: string | undefined): boolean {
  const methods = (allowedMethods ?? "")
    .split(",")
    .map((m) => m.trim().toUpperCase())
    .filter(Boolean);
  return methods.length > 0 && methods.every((m) => m === "ACH");
}

/**
 * Build the Rainforest `allowed-methods` string for the patient payment-link
 * page. Wallet tokens (Apple/Google Pay) are single-use and cannot back a
 * recurring, merchant-initiated draw, so they are suppressed whenever the saved
 * card must be re-chargeable — payment plans and membership charges. One-time
 * links keep the wallet option when the tenant allows it.
 */
export function buildPaymentLinkAllowedMethods({
  tenantAllowsApplePay,
  tenantAllowsAch,
  isPaymentPlan,
  hasMembershipCharges,
}: {
  tenantAllowsApplePay: boolean;
  tenantAllowsAch: boolean;
  isPaymentPlan: boolean;
  hasMembershipCharges: boolean;
}): string {
  const methods = ["CARD"];
  if (tenantAllowsAch) methods.push("ACH");
  if (!isPaymentPlan && !hasMembershipCharges && tenantAllowsApplePay) {
    methods.push("APPLE_PAY");
  }
  return methods.join(",");
}

/** Single-use wallet-token method identifiers that can't back a recurring draw. */
const WALLET_METHODS = new Set(["APPLE_PAY", "GOOGLE_PAY"]);

/**
 * Remove wallet tokens (Apple/Google Pay) from an `allowed-methods` string.
 * Save-on-file flows use this to guarantee the stored method is re-chargeable,
 * regardless of what a caller requests. Falls back to CARD if nothing
 * re-chargeable remains.
 */
export function stripWalletMethods(allowedMethods: string): string {
  const filtered = allowedMethods
    .split(",")
    .map((m) => m.trim())
    .filter((m) => m && !WALLET_METHODS.has(m.toUpperCase()));
  return filtered.length ? filtered.join(",") : "CARD";
}

/**
 * Filter a list of saved payment methods down to ones safe to bill on a
 * recurring, merchant-initiated schedule. Wallet-token pms (Apple/Google Pay)
 * can't back a repeating draw and must be excluded from any UI that lets staff
 * attach a saved method to a membership or payment plan.
 */
export function stripWalletPaymentMethods<T extends { type?: string | null }>(
  paymentMethods: T[],
): T[] {
  return paymentMethods.filter(
    (pm) => !pm.type || !WALLET_METHODS.has(pm.type.toUpperCase()),
  );
}
